import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Tooltip } from "@mui/material"
import React, { useEffect, useState } from "react"
import { CalculTypeEnum, calculTypeEnumLabelsOption } from "../../../core/enum/beem-shot/CalculTypeEnum"

interface IProps<T extends string> {
  id: string
  value: T
  handleChange(event: React.ChangeEvent<HTMLInputElement>): void
  handleSubmit?(): void
  disabled?: boolean
}

export function TypeCalculRadioButton<T extends string>({
  id,
  value,
  handleChange,
  handleSubmit,
  disabled = false,
}: Readonly<IProps<T>>): React.JSX.Element {
  const [isInitialized, setIsInitialized] = useState(false)

  function internalHandleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    event.target.id = id
    handleChange(event)
  }

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setIsInitialized(true)
    }
  }, [value])

  useEffect(() => {
    if (handleSubmit && isInitialized) {
      handleSubmit()
    }
  }, [value])

  function getTooltipText(type: CalculTypeEnum): React.JSX.Element | undefined {
    let text
    if (type === CalculTypeEnum.LOW_CARBON) {
      text = (
        <>
          🟢 Optimisé
          <br />
          Utilise les données les plus précises disponibles pour simuler un calcul proche d’une étude carbone détaillée par
          un bureau d'études.
        </>
      )
    } else if (type === CalculTypeEnum.STANDARD) {
      text = (
        <>
          🟡 Standard
          <br />
          Standard Recommandé en phase amont. Mélange de données par défaut, collectives et individuelles pour estimer
          l’impact carbone avec une marge de 5 à 10% d'un résultat optimisé par un bureau d'études.
        </>
      )
    } else if (type === CalculTypeEnum.HIGH_CARBON) {
      text = (
        <>
          🔴 Défavorable
          <br />
          Utilise uniquement des données génériques surévaluées pour anticiper un scénario pessimiste avec une large marge de
          sécurité.
        </>
      )
    }

    return text
  }

  return (
    <FormControl
      disabled={disabled}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        "& .MuiSvgIcon-root": {
          fontSize: { lg: 20, xl: 24 },
        },
      }}>
      <FormLabel
        id={`${id}-label`}
        sx={{
          fontFamily: "Poppins",
          fontSize: { lg: 12, xl: 14 },
          fontWeight: 500,
          lineHeight: "24px",
          letterSpacing: "0.15px",
          textAlign: "left",
        }}>
        Type d'impact
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={`${id}-radio-buttons-group`}
        name="radio-buttons-group"
        value={`${value}`}
        onChange={internalHandleChange}>
        {calculTypeEnumLabelsOption.map((option) => (
          <Tooltip key={option.value} title={getTooltipText(option.value)} arrow placement="top">
            <FormControlLabel
              value={option.value}
              control={
                <Radio
                  sx={{
                    padding: { lg: "4px", xl: "8px" },
                  }}
                />
              }
              label={option.label}
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: { lg: 20, xl: 24 },
                },
                "& .MuiTypography-root": {
                  fontSize: { lg: 12, xl: 14 },
                },
                "&.MuiFormControlLabel-root": {
                  marginRight: { lg: 3, xl: 4 },
                },
              }}
            />
          </Tooltip>
        ))}
      </RadioGroup>
    </FormControl>
  )
}
